import React, { useState } from 'react';
import TopBar from '../../Components/TopBar';
import Page from '../../Components/Containers/Page';
import { groupStatsByType, typeClasses, broadcastIcons, useBroadcasts } from '../../Data/Broadcasts';
import { PageLoading } from '../../Components/Loading';
import NoItems from '../../Components/EmptyStates/NoItems';
import AddEditBroadcast from './AddEditBroadcast';
import { Link } from 'react-router-dom';
import { getEntityLink, plural, useRedirect } from '../../Services/helpers';
import Icon from '../../Components/Icons/Icon';
import Button from '../../Components/Button/Button';

const Broadcasts = () => {
    const {broadcasts, addBroadcast} = useBroadcasts(true);
    const [isAdding, setIsAdding] = useState(false);
    const {go, Redirect} = useRedirect();

    return <>
        <TopBar left="/more"
            title="Broadcasts"
            right={<Button theme="light" icon="Plus" onClick={()=>setIsAdding(true)} zeroMargin />}
        />

        <Page>
            {broadcasts ? <Inner {...{broadcasts, setIsAdding}} /> : <PageLoading />}
        </Page>

        <AddEditBroadcast isOpen={isAdding}
            setIsOpen={setIsAdding}
            save={addBroadcast}
            then={broadcast => go(getEntityLink('Broadcasts/Broadcast', broadcast.broadcastId))}
        />

        <Redirect />
    </>
}

const Inner = ({broadcasts, setIsAdding}) => {
    const max = broadcasts.reduce((acc, broadcast) => {
        const total = Object.values(broadcast.stats).reduce((acc, stat) => acc + stat, 0);
        return total > acc ? total : acc;
    }, 0);

    return <>
        <ul className="divide-y divide-gray-200">
            {broadcasts.map(broadcast => {
                return <li key={broadcast.id} className="">
                    <Link to={getEntityLink('Broadcasts/Broadcast', broadcast.id)}
                        className="flex flex-row items-center justify-between gap-x-6 py-5"
                    >
                        <div className="shrink-0 flex flex-col items-center justify-between text-muted fill-muted">
                            <Icon icon={broadcastIcons[broadcast.status]} size="2x" />
                            <p className="text-sm mt-2">{broadcast.status}</p>
                        </div>
                        <div className="grow">
                            {broadcast.title ?? broadcast.id}
                            <Summary broadcast={broadcast} max={max} />
                        </div>
                        <div className="shrink-0 fill-muted">
                            <Icon icon="RightChevron" size="2x" />
                        </div>
                    </Link>
                </li>
            })}
        </ul>

        <NoItems title="Broadcasts"
            icon="Bullhorn"
            message={<>
                Send a message to some or all of your customers.<br />
                This is a great way to let them know about new services or changes to your business.
            </>}
            onClick={setIsAdding}
        />
    </>
}

const Summary = ({broadcast, max}) => {
    const total = Object.keys(broadcast.stats).reduce((acc, stat) => acc + broadcast.stats[stat], 0);

    if (total === 0) {
        return <p className="mt-2 text-muted">Add contacts to send this broadcast</p>
    }

    const groups = groupStatsByType(broadcast.stats);

    return <>
        <div className="mt-4 h-6 w-full md:w-[80%] flex flex-row overflow-hidden rounded-full border bg-slate-50 last:rounded-r-full">
            <Bar type={'Unavailable'} stat={groups.Unavailable} max={total} />
            <Bar type={'Pending'} stat={groups.Pending} max={total} />
            <Bar type={'Successful'} stat={groups.Successful} max={total} />
            <Bar type={'Error'} stat={groups.Error} max={total} />
        </div>
        <p className="mt-2 text-sm text-muted">{plural(total, 'No customers', 'One customer', `${total} customers`)} in broadcast</p>
    </>
}

const Bar = ({type, stat, max}) => {
    const width = stat / max * 100;

    return <div className={`relative h-full flex items-top justify-around ${typeClasses[type].bg} overflow-hidden`}
        style={{width: `${width}%`}}
    >
        {width > 10 && <span className="text-xs text-white text-center leading-6">{stat} {type}</span>}
    </div>
}

export default Broadcasts;